import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import { ApiError, getAlerts, getCountyBoundaries, getCounties, getVoivodeshipBoundaries } from "./api";
import { ensureSession } from "./auth";
import {
  activeCategories,
  boundariesByTeryt,
  countyNameByTeryt,
  currentAlerts,
  setCurrentAlerts,
  voivodeshipBoundariesByTeryt,
} from "./state";
import type { Category } from "./types";
import { initMapSearch, initMapView, renderAlertLayer, renderBaseCounties, renderVoivodeshipBorders } from "./mapView";
import {
  hideRegionDetail,
  initRegionDetail,
  isOpen,
  renderRegionDetail,
  showAllAlertsNationwide,
  showRegionDetail,
} from "./regionDetail";
import { hideMyAlerts, initMyAlerts, isMyAlertsOpen, renderMyAlerts } from "./myAlerts";
import { initSubscriptions } from "./subscriptions";
import { initThresholdHelp } from "./thresholdHelp";

const ALERT_REFRESH_INTERVAL_MS = 5 * 60_000;

let startupErrorEl: HTMLElement | null = null;
const startupErrorsByReason = new Map<string, string>();

function renderStartupErrors(): void {
  if (startupErrorsByReason.size === 0) {
    startupErrorEl?.remove();
    startupErrorEl = null;
    return;
  }
  if (!startupErrorEl) {
    startupErrorEl = document.createElement("div");
    startupErrorEl.className = "startup-error-banner";
    document.body.appendChild(startupErrorEl);
  }
  startupErrorEl.textContent = [...startupErrorsByReason.values()].join(" ");
}

function showStartupError(reason: string, message: string): void {
  startupErrorsByReason.set(reason, message);
  renderStartupErrors();
}

function hideStartupError(reason: string): void {
  startupErrorsByReason.delete(reason);
  renderStartupErrors();
}

function updateCounts(): void {
  document.getElementById("count-meteo")!.textContent = String(countAlertsByCategory("meteorological"));
  document.getElementById("count-hydro")!.textContent = String(countAlertsByCategory("hydrological"));
}

function countAlertsByCategory(category: Category): number {
  return currentAlerts.filter((alert) => alert.category === category).length;
}

function renderAll(): void {
  renderAlertLayer();
  updateCounts();
  if (isOpen()) {
    renderRegionDetail();
  }
  if (isMyAlertsOpen()) {
    void renderMyAlerts();
  }
}

let alertRefreshSeq = 0;

async function refreshAlerts(): Promise<void> {
  const seq = ++alertRefreshSeq;
  try {
    const alerts = await getAlerts();
    if (seq !== alertRefreshSeq) return;
    setCurrentAlerts(alerts);
    renderAll();
    hideStartupError("alerts");
  } catch (error) {
    if (seq !== alertRefreshSeq) return;
    console.error("Nie udało się odświeżyć ostrzeżeń:", error instanceof ApiError ? error.message : error);
    showStartupError("alerts", "Brak połączenia z serwerem — ostrzeżenia mogą być nieaktualne.");
  }
}

function wireTabs(): void {
  document.querySelectorAll<HTMLButtonElement>(".tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      const category = tab.dataset.category as Category;
      const wasActive = activeCategories.has(category);
      activeCategories.clear();
      document.querySelectorAll(".tab").forEach((t) => t.classList.remove("active"));
      if (!wasActive) {
        activeCategories.add(category);
        tab.classList.add("active");
      }
      renderAll();
    });
  });
}

// Non-modal overlay panels (region detail / moje alerty) don't block
// interaction with the map behind them, so they don't get a full focus trap
// like the settings/threshold-help Bootstrap modals — but Escape-to-close is
// still expected everywhere. Settings' own Escape handling is Bootstrap's.
function wireGlobalEscape(): void {
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (isOpen()) hideRegionDetail();
    else if (isMyAlertsOpen()) hideMyAlerts();
  });
}

function wirePanelCollapse(): void {
  const panel = document.getElementById("panel")!;
  const toggle = document.getElementById("panel-collapse-toggle")!;
  toggle.addEventListener("click", () => {
    const collapsed = panel.classList.toggle("collapsed");
    toggle.textContent = collapsed ? "+" : "−";
    toggle.title = collapsed ? "Rozwiń panel" : "Zwiń panel";
    toggle.setAttribute("aria-label", toggle.title);
  });
}

async function init(): Promise<void> {
  try {
    await ensureSession();
  } catch (error) {
    console.error("Nie udało się zalogować po powrocie z Casdoor:", error);
    showStartupError("auth", "Logowanie nie powiodło się. Odśwież stronę lub zaloguj się ponownie.");
  }

  initMapView("map", {
    onCountySelected: (terytCode) => {
      hideMyAlerts();
      showRegionDetail(terytCode);
    },
  });

  try {
    const [boundaries, counties, voivodeshipBoundaries] = await Promise.all([
      getCountyBoundaries(),
      getCounties(),
      getVoivodeshipBoundaries(),
    ]);
    for (const feature of boundaries.features) {
      boundariesByTeryt.set(feature.properties.teryt_code, feature);
    }
    for (const county of counties) {
      countyNameByTeryt.set(county.teryt_code, county.name);
    }
    for (const feature of voivodeshipBoundaries.features) {
      voivodeshipBoundariesByTeryt.set(feature.properties.teryt_code, feature);
    }
    renderBaseCounties();
    renderVoivodeshipBorders();
  } catch (error) {
    console.error("Nie udało się wczytać granic powiatów/województw:", error instanceof ApiError ? error.message : error);
    showStartupError("boundaries", "Brak połączenia z serwerem — część funkcji (mapa powiatów, wyszukiwanie) może nie działać. Odśwież stronę, aby spróbować ponownie.");
  }

  // Reszta inicjalizacji (ustawienia, moje alerty, pomoc) nie zależy od granic
  // powiatów — ma działać nawet gdy powyższe zapytanie się nie powiodło.
  initMapSearch("map-search-input", "map-search-results");

  initRegionDetail({
    panel: "region-detail",
    regionName: "region-name",
    alertsContainer: "region-detail-alerts",
    closeButton: "region-detail-close",
    modeRegionButton: "mode-region",
    modeAllButton: "mode-all",
  });

  initSubscriptions({
    settingsPanel: "settings",
    settingsBackdrop: "settings-backdrop",
    settingsToggle: "settings-toggle",
    settingsClose: "settings-close",
    settingsContent: "settings-content",
    loginGate: "settings-login-gate",
    loginButton: "settings-login-button",
    logoutButton: "settings-logout",
    form: "subscription-form",
    formError: "subscription-form-error",
    formSuccess: "subscription-form-success",
    nameInput: "webhook-name",
    urlInput: "webhook-url",
    voivodeshipSelect: "voivodeship-select",
    countySearchInput: "county-search",
    countySearchResults: "county-search-results",
    selectedCounties: "selected-counties",
    notifyMeteo: "notify-meteo",
    notifyHydro: "notify-hydro",
    submitButton: "subscription-submit",
    cancelEditButton: "subscription-cancel-edit",
    list: "subscription-list",
    addImgw: "subscription-add-imgw",
    addForecast: "subscription-add-forecast",
    emptyState: "subscription-empty-state",
    imgwSection: "imgw-section",
    forecastSection: "forecast-section",
    locationSearchInput: "location-search",
    locationSearchResults: "location-search-results",
    locationGroups: "location-groups",
    forecastDaysAhead: "forecast-days-ahead",
    onSaved: () => void refreshAlerts(),
  });

  initThresholdHelp({
    openButtons: ["help-toggle", "threshold-help-btn"],
    modal: "threshold-help-modal",
    closeButton: "threshold-help-close",
    body: "threshold-help-body",
  });

  document.getElementById("show-all-toggle")!.addEventListener("click", () => {
    hideMyAlerts();
    showAllAlertsNationwide();
  });

  const panelEl = document.getElementById("panel")!;
  initMyAlerts({
    panel: "my-alerts-panel",
    list: "my-alerts-list",
    closeButton: "my-alerts-close",
    toggleButton: "my-alerts-toggle",
    onShow: () => {
      hideRegionDetail();
      panelEl.classList.remove("collapsed");
    },
  });

  wireTabs();
  wirePanelCollapse();
  wireGlobalEscape();

  await refreshAlerts();
  setInterval(() => void refreshAlerts(), ALERT_REFRESH_INTERVAL_MS);
}

void init();
